import axios from "axios";
import config from "@/config/config.js";

// Contexte de l'appareil, pour guider l'utilisateur (notamment sur iPhone)
export const isIOS = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
export const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;
export const pushSupported = () =>
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;
export const permission = () =>
  "Notification" in window ? Notification.permission : "unsupported";

export function deviceLabel() {
  const ua = navigator.userAgent;
  const device = /iphone/i.test(ua)
    ? "iPhone"
    : /ipad/i.test(ua)
      ? "iPad"
      : /android/i.test(ua)
        ? "Android"
        : /mac/i.test(ua)
          ? "Mac"
          : /windows/i.test(ua)
            ? "PC Windows"
            : /linux/i.test(ua)
              ? "Linux"
              : "Appareil";
  const browser = /edg\//i.test(ua)
    ? "Edge"
    : /firefox|fxios/i.test(ua)
      ? "Firefox"
      : /chrome|crios/i.test(ua)
        ? "Chrome"
        : /safari/i.test(ua)
          ? "Safari"
          : "";
  return `${device}${browser ? ` · ${browser}` : ""}${isStandalone() ? " (app)" : ""}`;
}

function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((char) => char.charCodeAt(0)));
}

export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register("/sw.js");
  } catch (error) {
    console.warn("Service worker non enregistré", error);
    return null;
  }
}

// Abonnement Web Push de ce navigateur, s'il existe
export async function currentSubscription() {
  if (!pushSupported()) return null;
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

// Demande l'autorisation, abonne ce navigateur et l'enregistre côté API
export async function subscribeThisDevice() {
  const { data: pushConfig } = await axios.get(
    `${config.apiUrl}/notifications/config`,
  );
  if (!pushConfig?.push_available)
    throw new Error(
      "Les notifications push ne sont pas encore configurées sur le serveur.",
    );
  const granted = await Notification.requestPermission();
  if (granted !== "granted")
    throw new Error(
      "Notifications refusées : autorisez-les dans les réglages du navigateur.",
    );
  const registration =
    (await registerServiceWorker()) && (await navigator.serviceWorker.ready);
  if (!registration)
    throw new Error("Ce navigateur ne permet pas les notifications.");
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(pushConfig.vapid_public_key),
    });
  }
  const { data } = await axios.post(`${config.apiUrl}/notifications/devices`, {
    subscription: subscription.toJSON(),
    label: deviceLabel(),
  });
  return data;
}

// Appareil de ce navigateur côté API (pour l'oublier à la déconnexion)
export const PUSH_DEVICE_ID_KEY = "prexens:push-device-id";

// Désabonne ce navigateur (et l'oublie côté API si l'identifiant est connu)
export async function unsubscribeThisDevice(deviceId) {
  const subscription = await currentSubscription();
  if (subscription) await subscription.unsubscribe();
  if (deviceId)
    await axios.delete(`${config.apiUrl}/notifications/devices/${deviceId}`);
}
